/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx, cssMap, cx } from '@compiled/react';

import DropdownMenu from '@atlaskit/dropdown-menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';

import { ToolbarDropdownItem } from './ToolbarDropdownItem';
import { ToolbarTooltip } from './ToolbarTooltip';

const styles = cssMap({
	scrollContainer: {
		maxHeight: '320px',
		overflowY: 'auto',
	},
});

type ToolbarNestedDropdownMenuProps = {
	children?: ReactNode;
	'data-extension-item-key'?: string;
	dropdownTestId?: string;
	elemAfter: ReactNode;
	elemAfterText?: ReactNode;
	elemBefore: ReactNode;
	/**
	 * Enforeces a max height of 320px for menus - when menu is larger a scroll is introduced
	 */
	enableMaxHeight?: boolean;
	isDisabled?: boolean;
	onClick?: (e: MouseEvent | KeyboardEvent) => void;
	shouldFitContainer?: boolean;
	shouldIgnoreCloseEvent?: (event: Event | MouseEvent | KeyboardEvent) => boolean;
	shouldTitleWrap?: boolean;
	testId?: string;
	text?: string;
	tooltipContent?: ReactNode;
};

export const ToolbarNestedDropdownMenu = ({
	elemBefore,
	text,
	elemAfterText,
	elemAfter,
	children,
	isDisabled,
	testId,
	dropdownTestId,
	enableMaxHeight = false,
	onClick,
	shouldFitContainer = false,
	shouldIgnoreCloseEvent,
	shouldTitleWrap,
	tooltipContent,
	'data-extension-item-key': dataExtensionItemKey,
}: ToolbarNestedDropdownMenuProps): JSX.Element => {
	return (
		<DropdownMenu<HTMLButtonElement>
			shouldFitContainer={shouldFitContainer}
			shouldIgnoreCloseEvent={fg('cc_blocks_changeboarding') ? shouldIgnoreCloseEvent : undefined}
			placement="right-start"
			testId={dropdownTestId}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			trigger={(triggerProps) => {
				const item = (
					<ToolbarDropdownItem
						elemBefore={elemBefore}
						elemAfter={elemAfter}
						isSelected={triggerProps.isSelected}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onClick={(e) => {
							onClick && onClick(e);
							triggerProps.onClick && triggerProps.onClick(e);
						}}
						testId={testId}
						triggerRef={triggerProps.triggerRef}
						hasNestedDropdownMenu={true}
						isDisabled={isDisabled}
						shouldTitleWrap={shouldTitleWrap}
						data-extension-item-key={
							fg('cc_blocks_changeboarding') ? dataExtensionItemKey : undefined
						}
					>
						{text}
						{elemAfterText}
					</ToolbarDropdownItem>
				);

				if (tooltipContent) {
					return (
						<ToolbarTooltip content={tooltipContent} position="top">
							{item}
						</ToolbarTooltip>
					);
				}

				return item;
			}}
		>
			<Box xcss={cx(enableMaxHeight && styles.scrollContainer)} data-toolbar-nested-dropdown-menu>
				{children}
			</Box>
		</DropdownMenu>
	);
};
